import type { TileDef } from '@md/engine';

/**
 * Légende de tuiles propre à Magnetic Duo (concept §7.2), en plus de la base
 * commune `# . _ @ P M p m` fournie par le moteur.
 *
 * | Caractère | Rôle |
 * |---|---|
 * | `s` | surface acier — Mag peut s'y accrocher |
 * | `c` | surface cuivre — Polo peut s'y accrocher |
 * | `+` | dalle électrifiée rouge — tue Mag |
 * | `-` | dalle électrifiée bleue — tue Polo |
 * | `!` | dalle d'inversion de polarité |
 * | `~` | zone dégaussante — les champs n'y fonctionnent pas |
 *
 * Toutes ces tuiles sont SOLIDES sauf `~` : ce sont des parois et des dalles
 * sur lesquelles on marche. Les règles qui les lisent (accroche, mort,
 * inversion) travaillent donc par CONTACT, jamais par recouvrement.
 */

/** Caractère de la surface d'accroche acier (Mag). */
export const TILE_STEEL_CHAR = 's';
/** Caractère de la surface d'accroche cuivre (Polo). */
export const TILE_COPPER_CHAR = 'c';
/** Dalle électrifiée rouge : mortelle pour Mag. */
export const TILE_CHARGED_RED_CHAR = '+';
/** Dalle électrifiée bleue : mortelle pour Polo. */
export const TILE_CHARGED_BLUE_CHAR = '-';
/** Dalle d'inversion de polarité. */
export const TILE_INVERTER_CHAR = '!';
/** Zone dégaussante : traversable, annule les pouvoirs magnétiques. */
export const TILE_DEGAUSS_CHAR = '~';

function tile(def: Partial<TileDef> & Pick<TileDef, 'char' | 'name'>): TileDef {
  return {
    solid: false,
    oneWay: false,
    lethal: false,
    marker: null,
    tint: null,
    ...def,
  };
}

export const TILE_STEEL = tile({
  char: TILE_STEEL_CHAR,
  name: 'surface acier (accroche Mag)',
  solid: true,
  tint: 0x5c6b82,
});

export const TILE_COPPER = tile({
  char: TILE_COPPER_CHAR,
  name: 'surface cuivre (accroche Polo)',
  solid: true,
  tint: 0xb5713a,
});

export const TILE_CHARGED_RED = tile({
  char: TILE_CHARGED_RED_CHAR,
  name: 'dalle électrifiée rouge (tue Mag)',
  solid: true,
  tint: 0x8a1f2c,
});

export const TILE_CHARGED_BLUE = tile({
  char: TILE_CHARGED_BLUE_CHAR,
  name: 'dalle électrifiée bleue (tue Polo)',
  solid: true,
  tint: 0x14567d,
});

export const TILE_INVERTER = tile({
  char: TILE_INVERTER_CHAR,
  name: "dalle d'inversion de polarité",
  solid: true,
  tint: 0x9b5de5,
});

export const TILE_DEGAUSS = tile({
  char: TILE_DEGAUSS_CHAR,
  name: 'zone dégaussante',
  tint: 0x5a3d8a,
});

export const TILE_LEGEND: Readonly<Record<string, TileDef>> = Object.freeze({
  [TILE_STEEL_CHAR]: TILE_STEEL,
  [TILE_COPPER_CHAR]: TILE_COPPER,
  [TILE_CHARGED_RED_CHAR]: TILE_CHARGED_RED,
  [TILE_CHARGED_BLUE_CHAR]: TILE_CHARGED_BLUE,
  [TILE_INVERTER_CHAR]: TILE_INVERTER,
  [TILE_DEGAUSS_CHAR]: TILE_DEGAUSS,
});
